import { ActionType, ErrorAction, RootAction } from '@/actions/actiontypes';
import { handleLostFocus, handleRegainedFocus } from '@/actions/metaActions';
import {
    createListOfRecentSearchQueries,
    LAST_VISITED_NOTIFICATIONS_STORAGE_KEY,
    PREFERENCES_STORAGE_KEY,
    RootState,
    VegkartError,
} from '@/state';
import { stateToHash } from '@/state/hash/StateToHash';
import { Dispatch } from '@/state/store';
import { getHash } from '@/utils/Utils';
import { Integration } from './integration';
import { isAction, type Middleware } from 'redux';

type AppMiddleware = Middleware<unknown, RootState, Dispatch>;

const isRootAction = (action: unknown): action is RootAction =>
    isAction(action) && Object.values(ActionType).includes(action.type as ActionType);

/**
 * Logs all actions and states after they are dispatched.
 */
/* tslint:disable:no-console */
export const logger: AppMiddleware = _ => next => action => {
    const isDevelopment = process.env.NODE_ENV === 'development';
    if (isDevelopment && isAction(action)) {
        const actionType =
            ActionType[action.type as keyof typeof ActionType] || action.type;
        console.group(actionType);
        console.info('dispatching', { ...action, type: actionType });
    }
    const result = next(action);
    if (isDevelopment && isAction(action)) {
        console.groupEnd();
    }
    return result;
};

/**
 * Changes URL hash if necessary after each action.
 */

export const hashChanger: AppMiddleware = store => next => action => {
    const actionCall = next(action);
    const state: RootState = store.getState();
    const currentURI = getHash(window.location.hash);
    const hashState = stateToHash(state);
    if (currentURI !== hashState) {
        window.location.replace(
            window.location.pathname.replace('//', '/') + '#' + hashState
        );
    }

    return actionCall;
};

// Handles synchronization of state in LocalStorage
export const persister: AppMiddleware = store => next => action => {
    const actionCall = next(action);
    const state: RootState = store.getState();
    if (!isRootAction(action)) {
        return actionCall;
    }
    switch (action.type) {
        case ActionType.LAST_VISITED_NOTIFICATIONS_UPDATED:
            localStorage.setItem(
                LAST_VISITED_NOTIFICATIONS_STORAGE_KEY,
                state.metaState.lastVisitedNotifications?.toISOString()
            );
            break;
        case ActionType.PREFERENCES_UPDATE:
        case ActionType.PREFERENCES_RESET:
        case ActionType.MAP_LAYER_SET:
            localStorage.setItem(
                PREFERENCES_STORAGE_KEY,
                JSON.stringify(state.preferences)
            );
            break;
        case ActionType.ADD_SEARCH_QUERY:
            const recentSearchQueries = JSON.parse(
                localStorage.getItem('recentSearchQueries') || '[]'
            );
            const updatedRecentSearchQueries = createListOfRecentSearchQueries(
                action.searchQuery,
                recentSearchQueries
            );
            localStorage.setItem(
                'recentSearchQueries',
                JSON.stringify(updatedRecentSearchQueries)
            );
            break;
    }
    return actionCall;
};

export function hashChangeEventListeners(dispatch: Dispatch) {
    return [
        { type: 'blur', handler: () => dispatch(handleLostFocus(window.location.hash)) },
        {
            type: 'focus',
            handler: () => dispatch(handleRegainedFocus(window.location.hash)),
        },
    ];
}

export function bindErrorCatcher(dispatch: Dispatch, integration: Integration): void {
    window.onerror = (
        message: string | Event,
        filename?: string,
        lineno?: number,
        colno?: number,
        error?: Error
    ) => {
        const vegkartError = new VegkartError('En feil har oppstått', {
            colno,
            error,
            filename,
            lineno,
            message: String(message),
        });
        const errorAction: ErrorAction = {
            type: ActionType.ERROR_OCCURRED,
            error: vegkartError,
        };
        dispatch(errorAction);
        integration.server.postErrorReport(vegkartError);
    };
}
